import React, { useEffect, useLayoutEffect, useState } from 'react'
import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'
import reportWebVitals from './reportWebVitals'
import { ThemeController } from '@embracesbs/helpers'
import { MsalProvider } from '@azure/msal-react'
import { Configuration, PublicClientApplication } from '@azure/msal-browser'
import AppLoading from './Components/Common/AppLoading'
import { ConfigProvider, useConfig } from './Components/Config/ConfigContext'
import { AuthProvider } from './Components/Context/AuthContext'
import { PreferencesProvider, usePreferences } from './Components/Context/PreferencesContext'
import PreferencesSync from './Components/Context/PreferencesSync'
import { resolveTheme } from './Theme/appThemes'
import { useMediaQuery } from './Hooks/useMediaQuery'

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement)

const Root = () => {
  return (
    <React.StrictMode>
      <PreferencesProvider>
        <ThemedRoot>
          <ConfigProvider>
            <MsalInstanceProvider>
              <AuthProvider>
                <PreferencesSync />
                <App />
              </AuthProvider>
            </MsalInstanceProvider>
          </ConfigProvider>
        </ThemedRoot>
      </PreferencesProvider>
    </React.StrictMode>
  )
}

// Applies the user's theme preference: the design-system theme object goes to
// ThemeController, the app-shell CSS variables flip via body[data-theme].
function ThemedRoot({ children }: { children: React.ReactNode }) {
  const { preferences } = usePreferences()
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)')
  const { themeObject, shellMode } = resolveTheme(preferences, systemPrefersDark)

  // useLayoutEffect: flip the shell variables before the browser paints, so
  // even the AppLoading screens (rendered as children, long before auth
  // exists) come up in the right theme without a flash. data-a11y drives the
  // status/semantic token overrides for the color-vision themes (index.css).
  useLayoutEffect(() => {
    document.body.dataset.theme = shellMode
    document.body.dataset.a11y = preferences.accessibilityTheme
  }, [shellMode, preferences.accessibilityTheme])

  return <ThemeController themeWholeObject={themeObject}>{children}</ThemeController>
}

function MsalInstanceProvider({ children }: { children: React.ReactNode }) {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const config = useConfig()

  useEffect(() => {
    const loadConfig = async () => {
      if (!config) return

      const msalConfig: Configuration = {
        auth: {
          clientId: config.authClientId,
          authority: config.authAuthority,
          redirectUri: window.location.origin
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false
        },
        system: {
          allowNativeBroker: false
        }
      }

      const instance = new PublicClientApplication(msalConfig)
      await instance.initialize()

      setMsalInstance(instance)
      setIsLoading(false)
    }

    loadConfig()
  }, [config])

  if (isLoading || !msalInstance) {
    return <AppLoading text='Authenticating…' />
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>
}

root.render(<Root />)
reportWebVitals()
